import { create } from 'zustand';
import { BALANCE_SHEET_PRESETS, showToast } from 'lib/utils';
import { subscribeWithSelector } from 'zustand/middleware';
import { getCategories, getDefaultCategory } from 'lib/honeTableUtils';
import { Category, DataCellState } from 'domain/models';
import { refreshReport, useReportsStore } from './useReportsStore';
import { useLocationsStore } from './useLocationsStore';
import {
  DismissedBalanceSheetPayload,
  extractReportIds,
  ReportRequest,
  ReportTimeframe,
  ReportType,
  ReportTypes,
} from '@hone-automation/common';
import { makeRemoteDismissSparklines } from 'main/factories/usecases/remote-dismiss-spaklines';

import { makeRemotePostReport } from 'main/factories/usecases/reports/remote-post-report';

import * as Sentry from '@sentry/react';

import { differenceInHours } from 'date-fns';
import * as tz from 'date-fns-tz';

import { trackError } from '../lib/analytics';
import { queryClient } from '../lib/queryClient';
import toast from 'react-hot-toast';
import { isEmpty } from 'lodash';
import { useQueryState } from './useQueryState';

const CategoryColorMapping: string[] = [
  'rgba(62, 99, 120, 0.3)',
  'rgba(137, 219, 246, 0.2)',
  'rgba(117,197,207,0.3)',
  'rgba(117, 126, 207, 0.1)',
  'rgba(242, 153, 74, 0.1)',
  'rgba(45, 156, 219, 0.1)',
  'rgba(168, 27, 78, 0.1)',
];

interface ActiveReportState {
  abortController: null | AbortController;
  refreshingReport: boolean;
  setRefreshingReport: (nextState: boolean) => void;
  chartLoading: boolean;
  smoothingEnabled: boolean;
  activeCategory: Category | undefined;
  allCategories: string[];
  currentReport: NestedHoneReport | undefined;
  currentReportId: string | undefined;
  status: 'idle' | 'loading' | 'refreshing';
  setStatus: (status: 'idle' | 'loading') => void;
  yearPlotted: number | undefined;
  rangeNoData: boolean;
  multilocationParamsEnabled: boolean;
  setYearPlotted: (year: number) => void;
  toggleSmoothing: () => void;
  dismissSparklines: (dismissPayload: DismissedBalanceSheetPayload) => void;
  getGroupReport: (dateRangeParams: ReportRequest, allowRefresh?: boolean) => Promise<NestedHoneReport | 'error'>;
  enableMultiLocation: boolean;
  setEnableMultiLocation: (nextState: boolean) => void;
  modalCellState: DataCellState;
  setDataCellState: (nextState: DataCellState) => void;
  currentDataCell: DataCellState | undefined;
  setCurrentDataCell: (nextState: DataCellState) => void;
  expandedRows: string[];
  setExpandedRows: (nextState: string[]) => void;
  reportPayload: ReportRequest | undefined;
  otherReportPayload: ReportRequest | undefined;
  setReportPayload: (nextState: ReportRequest | undefined) => void;
  setOtherReportPayload: (nextState: ReportRequest | undefined) => void;
  updateDate: Date | undefined;
  resetUpdatedDate: () => void;
}

const postDismissSparklines = makeRemoteDismissSparklines();
const postReportGetByIdOrGrouped = makeRemotePostReport();

export const initialDataCellState: DataCellState = {
  modalOpen: false,
  audit: null,
  dataRows: [],
  date: {
    start: '',
    end: '',
  },
  title: '',
  parent: '',
  total: 0,
  loading: false,
  hasDataRowsOrAudit: false,
};

export const useActiveReportStore = create(
  subscribeWithSelector<ActiveReportState>((set, get) => {
    return {
      updateDate: undefined,
      abortController: null,
      refreshingReport: false,
      smoothingEnabled: false,
      activeCategory: undefined,
      allCategories: [],
      currentReport: undefined,
      currentReportId: undefined,
      status: 'idle',
      yearPlotted: new Date().getFullYear(),
      rangeNoData: false,
      chartLoading: false,
      enableMultiLocation: false,
      setEnableMultiLocation: (nextState: boolean) => set({ enableMultiLocation: nextState }),
      setRefreshingReport: (nextState: boolean) => set({ refreshingReport: nextState }),
      setStatus: (status: 'idle' | 'loading') => set({ status }),
      setYearPlotted: year => set({ yearPlotted: year }),
      toggleSmoothing: () => set(state => ({ smoothingEnabled: !state.smoothingEnabled })),
      multilocationParamsEnabled: false,
      modalCellState: initialDataCellState,
      setDataCellState: nextState => set({ modalCellState: nextState }),
      currentDataCell: undefined,
      expandedRows: [],
      setExpandedRows: nextState => set({ expandedRows: nextState }),
      setCurrentDataCell: nextState => set({ currentDataCell: nextState }),
      reportPayload: undefined,
      otherReportPayload: undefined,
      setReportPayload: nextState => set({ reportPayload: nextState }),
      setOtherReportPayload: nextState => set({ otherReportPayload: nextState }),
      setChartLoading: (nextState: boolean) => set({ chartLoading: nextState }),
      dismissSparklines: async (dismissPayload: DismissedBalanceSheetPayload) => {
        try {
          const { locationId, title } = dismissPayload;

          if (!title || !locationId) {
            throw new Error('title or locationId not available');
          }
          await postDismissSparklines.dismiss(dismissPayload);
        } catch (error) {
          Sentry.captureException(error);
        }
      },
      resetUpdatedDate: () => set({ updateDate: undefined }),
      getGroupReport: async (
        payload: ReportRequest,
        allowRefresh: boolean = true
      ): Promise<NestedHoneReport | 'error'> => {
        try {
          // Abort previous request if it exists
          const prevController = get().abortController;

          if (prevController) {
            prevController.abort();
          }

          // Create a new AbortController for the new request
          const controller = new AbortController();

          set({ abortController: controller });
          const urlParams = new URLSearchParams(window.location.search);
          const validate = urlParams.get('validate');
          // Clear current report to prevent showing stale data, but preserve during validation
          // so the table stays visible while validation panel loads
          const preserveReport = validate ? get().currentReport : undefined;
          useActiveReportStore.setState({ status: 'loading', rangeNoData: false, currentReport: preserveReport });

          const timeframe = urlParams.get('timeframe');
          const reverse = urlParams.get('reverse');
          const consolidated = urlParams.get('consolidated');
          const breakdownPeriods = urlParams.get('breakdownPeriods');
          const budget = urlParams.get('budget');
          const compareLocations = urlParams.get('compareLocations');
          const type = urlParams.get('type');

          const { startDate, baseId } = payload;
          if (startDate === undefined && timeframe !== 'Year to Date' && !baseId) {
            throw new Error('startDate, endDate are required');
          }

          if (budget === 'true') {
            payload.budgets = { include: budget === 'true' };
          }
          if (timeframe) {
            payload.timeframe = timeframe as ReportTimeframe;
          }

          if (payload.comparison?.comparisonPeriods) {
            delete payload.endDate;
          }

          if (consolidated) {
            payload.consolidated = consolidated === 'true';
          }

          if (reverse === 'true') {
            payload.reverse = reverse === 'true';
          }

          if (timeframe === 'Year to Date') {
            delete payload.startDate;
            delete payload.endDate;
          }

          if (breakdownPeriods === 'true') {
            payload.breakdownPeriods = true;
          }

          if (type) {
            payload.type = type as ReportType;
          }
          const isAggrSideBySide =
            compareLocations && (!consolidated || consolidated === 'false')
              ? compareLocations?.split(',').length > 1
              : false;

          if (isAggrSideBySide) {
            delete payload.breakdownPeriods;
            delete payload.difference;
            delete payload.total;
            delete payload.reverse;
            delete payload.comparison;
          }

          if (validate) {
            payload.postTransforms = {
              validate: true,
            };
          }

          const response = (await postReportGetByIdOrGrouped.postReport(payload, {
            signal: controller.signal,
          })) as NestedHoneReport;

          if (controller.signal.aborted) return 'error';

          const nowUTC = tz.toDate(new Date(), { timeZone: 'UTC' });
          const updatedAtUTC = response.updatedAt ? tz.toDate(response.updatedAt, { timeZone: 'UTC' }) : undefined;
          const selectedSummaryReport = useReportsStore.getState().selectedReport;
          const selectedSummaryReportDate = selectedSummaryReport?.updatedAt
            ? tz.toDate(selectedSummaryReport.updatedAt, { timeZone: 'UTC' })
            : undefined;

          const compareUpdatedAt = updatedAtUTC || selectedSummaryReportDate || nowUTC;

          const hoursSinceLastUpdate = differenceInHours(nowUTC, compareUpdatedAt);
          if (hoursSinceLastUpdate > 6 && allowRefresh) {
            useActiveReportStore.setState({ status: 'loading' });

            let reportsIds = extractReportIds(response.dates);
            if (isEmpty(reportsIds) && baseId) {
              reportsIds = [baseId!];
            }
            const refresh = refreshReport(reportsIds, { signal: controller.signal })
              .then(async refreshedResponse => {
                if (controller.signal.aborted) return;
                // Fetch the refreshed report data after the refresh completes
                const refreshedReport = await get().getGroupReport(payload, false);
                useActiveReportStore.setState({
                  currentReport: refreshedReport !== 'error' ? refreshedReport : response,
                  status: 'idle',
                  updateDate: nowUTC,
                  refreshingReport: false,
                });
                return response;
              })
              .catch(async error => {
                // On error, just use the original response and don't try to refetch
                useActiveReportStore.setState({
                  currentReport: response as NestedHoneReport,
                  refreshingReport: false,
                  status: 'idle',
                });
                trackError({ error: error as Error });
              });

            await toast.promise(refresh, {
              loading: 'Refreshing report, will take a few seconds to refresh',
              success: 'Report, successfully refreshed',
              error: 'Error refreshing report',
            });
          } else {
            useActiveReportStore.setState({
              currentReport: response as NestedHoneReport,
              status: 'idle',
            });
          }

          set({ abortController: null });
          return response as NestedHoneReport;
        } catch (error: any) {
          console.error('Error en getGroupReport:', error);
          useActiveReportStore.setState({ status: 'idle', currentReport: undefined });
          showToast(error.message, 'error', 5000);
          Sentry.captureException(error);
          return 'error';
        } finally {
          useActiveReportStore.setState({ status: 'idle' });
          set({ abortController: null });
        }
      },
    };
  })
);

/**
 Subscribes to currentReport, after every change will update categories for currentReport,
 and set the first one as active
 */
function getCategoriesFromReport(currentReport: NestedHoneReport | undefined) {
  if (!currentReport) return;

  const allCategories = getCategories(currentReport.sections);

  if (allCategories.length === 0) {
    allCategories[0] = getDefaultCategory(currentReport.sections);
  }

  const activeCategory = { name: allCategories[0], color: CategoryColorMapping[0] };
  useActiveReportStore.setState({ allCategories, activeCategory });
}

useActiveReportStore.subscribe(state => state.currentReport, getCategoriesFromReport);

type Overrides = { [key: string]: boolean };

function containsSubstring(largeString: string, substrings: string[]): boolean {
  const urlParams = new URLSearchParams(largeString);
  const reportType = urlParams.get('type');

  if (reportType === 'ytd--income-statement') return false;

  for (const substring of substrings) {
    if (largeString.includes(substring)) {
      return true;
    }
  }
  return false;
}

/**
 * Subscribes to currentReportId and selectedReport to fetch the full report by id
 * @param report
 */
export async function fetchReportById(report?: HoneReportSummary) {
  try {
    const useActiveReportStoreState = useActiveReportStore.getState();
    const existingReportPayload = useActiveReportStoreState.reportPayload;
    const prevReportPayload = JSON.stringify(existingReportPayload);
    const nbs = containsSubstring(window.location.href, Object.keys(BALANCE_SHEET_PRESETS));
    const refreshingReport = useActiveReportStoreState.refreshingReport;
    const audit = new URLSearchParams(window.location.search).get('audit');
    const configuration = new URLSearchParams(window.location.search).get('configuration');
    if (nbs && !refreshingReport) {
      return;
    }

    if (audit) {
      return;
    }

    // Ensure report exists and has an id before proceeding
    if (!report || !report.id) {
      console.error('Report is missing id property');
      return;
    }

    const reportId = report.id;

    const urlParams = new URLSearchParams(window.location.search);
    const locationId = useLocationsStore.getState().currentLocation?.id;
    const reportType = urlParams.get('type');
    const reportTypePayload = reportType || useReportsStore.getState().selectedReport?.type;
    const validate = urlParams.get('validate');

    if (!reportId || !locationId) {
      throw new Error('reportId or locationId not available');
    }
    // Preserve currentReport during validation so the table stays visible
    const preserveReport = validate ? useActiveReportStoreState.currentReport : undefined;
    useActiveReportStore.setState({ refreshingReport: true, status: 'loading', currentReport: preserveReport });

    // Use existing reportPayload if available, otherwise create a new one
    let payload: ReportRequest;
    if (existingReportPayload && configuration === 'Vendor+Invoices') {
      payload = { ...existingReportPayload };
    } else {
      payload = {
        baseId: reportId,
        locations: [String(locationId)],
        type: reportTypePayload as ReportType,
      };
    }

    if (validate) {
      payload.postTransforms = { validate: true };
    }

    // Skip if payload is identical to previous (but always run if validating)
    if (JSON.stringify(payload) === prevReportPayload && !validate) {
      return;
    }
    useActiveReportStore.setState({ reportPayload: payload, otherReportPayload: payload });
    await useActiveReportStoreState.getGroupReport(payload, true);
  } catch (error) {
    Sentry.captureException(error);
    trackError({ error: error as Error });
  } finally {
    useActiveReportStore.setState({ refreshingReport: false, status: 'idle' });
  }
}

// TODO review this for refactor
useReportsStore.subscribe(
  state => state.selectedReport,
  (reportSummary, prevReportSummary) => {
    // Skip if the report hasn't actually changed (same id)
    if (reportSummary?.id === prevReportSummary?.id) {
      return;
    }

    if (reportSummary?.type === 'P&L Comparison') {
      useActiveReportStore.setState({ reportPayload: undefined });
      return;
    }
    fetchReportById(reportSummary);
  }
);

export const performReportUpdate = async (reportSummary: HoneReportSummary, reportsIds: string[]) => {
  // Abort previous request if it exists
  useActiveReportStore.setState({ status: 'loading' });
  const prevController = useActiveReportStore.getState().abortController;
  const prevPayload = useActiveReportStore.getState().reportPayload;
  if (prevController) {
    prevController.abort();
  }
  // Create a new AbortController for the new request
  const controller = new AbortController();

  useActiveReportStore.setState({ refreshingReport: true, abortController: controller, currentReport: undefined });
  const currentLocation = useLocationsStore.getState().currentLocation;
  const currentLocationId =
    currentLocation?.link && currentLocation?.link !== '' ? currentLocation?.link : currentLocation?.id;
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const params = new URL(document.location).searchParams;
  const allReports = !!params.get('allReports') || false;
  const reportPayload: ReportRequest =
    reportSummary?.type === 'P&L Comparison'
      ? prevPayload!
      : {
          baseId: reportSummary.id,
          locations: [currentLocationId!],
          type: reportSummary.type as ReportType,
        };
  useActiveReportStore.setState({
    reportPayload: reportPayload,
  });
  const refresh = refreshReport(reportsIds, { signal: controller.signal })
    .then(async (response: any) => {
      if (controller.signal.aborted) return;
      if (response.status === 200) {
        Promise.all([
          queryClient.refetchQueries({
            queryKey: ['reportsSummary', currentLocationId, allReports],
            exact: true,
          }),
          useActiveReportStore.getState().getGroupReport(prevPayload!, false),
        ]).then(() => {
          const data: HoneReportSummary[] | undefined = queryClient.getQueryData([
            'reportsSummary',
            currentLocationId,
            allReports,
          ]);
          const nextSelectedReport = data?.find(({ id }) => id === reportSummary.id);
          useReportsStore.setState({ selectedReport: nextSelectedReport });
          useActiveReportStore.setState({ refreshingReport: false, abortController: null });
        });
      }
      return response;
    })
    .catch(error => {
      trackError({ error: error as Error });
    });
  await toast.promise(
    refresh,
    {
      loading: 'Refreshing report, will take a few seconds to refresh',
      success: 'Report, successfully refreshed',
      error: 'Error refreshing report',
    },
    {
      style: {
        maxWidth: 'max(50vw, 350px)',
      },
      success: {
        duration: 4000,
      },
    }
  );
  useActiveReportStore.setState({ status: 'idle' });
};

function removeAuditReport() {
  useReportsStore.setState({ auditReport: undefined });
}

useActiveReportStore.subscribe(state => state.currentReportId, removeAuditReport);
